import { toastStore, type ActionItem } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { type RowClient } from 'entities/Clients/model/RowClient'
import { ClientsApi } from 'entities/Clients/api/clients'
import { ClientsPath } from 'entities/ExportRouters'
import { rename } from 'pages/clients/ui/actions/rename/rename'
import { assumeClient } from 'pages/clients/ui/actions/assumeClient/assumeClient'
import { manageTeams } from 'pages/clients/ui/actions/manageTeams/manageTeams'
import { type ActiveClientsTabStore } from 'pages/clients/store/activeClientsTabStore'
import { deleteClient } from 'pages/clients/ui/actions/deleteClient/deleteClient'

export const CLIENT_ROW_GROUP_ORDER = ['HubSpot', 'Actions']

export const getClientRowActions = (
  row: RowClient,
  activeClientsTabStore: ActiveClientsTabStore
): ActionItem[] => {
  const isHipaaCompliant = row.origin.is_hipaa_compliant
  const {
    isGroupsFlowEnabled,
    integrationsTeamsList,
    loadData,
    isHubSpotConnected,
    loadIntegrationsTeamsList,
  } = activeClientsTabStore
  const canShowHubSpotActions = isGroupsFlowEnabled && isHubSpotConnected
  const canShowRefreshData = canShowHubSpotActions && row.groups.length > 0

  const assumeAction: ActionItem = {
    text: 'Assume',
    icon: 'eye',
    onAction: () => {
      if (isHipaaCompliant) return
      return assumeClient(row.origin.owner_id)
    },
    buttonProps: {
      disabled: isHipaaCompliant,
      tooltipProps: {
        label: 'Assume feature is disabled for HIPAA-compliant clients.',
        breakWord: true,
        placement: 'top',
        maxWidth: '190px',
        visibility: isHipaaCompliant ? 'visible' : 'hidden',
        withoutPointer: true,
        fullWidth: true,
      },
    },
  }

  const manageTeamsAction: ActionItem = {
    text: 'Manage teams',
    icon: 'users2',
    group: 'HubSpot',
    onAction: () => manageTeams(row, loadData, integrationsTeamsList),
  }

  const refreshDataAction: ActionItem = {
    text: 'Refresh data',
    icon: 'refresh',
    group: 'HubSpot',
    onAction: async () => {
      try {
        await Promise.all([
          ClientsApi.refreshHubSpotTeams(row.origin.id),
          ClientsApi.refreshHubSpotTeamsContacts(row.origin.id),
        ])
        await Promise.all([loadData(), loadIntegrationsTeamsList()])
        toastStore.add({
          type: 'success',
          title: 'HubSpot data refreshed',
        })
      } catch (error) {
        logger.error(error)
        toastStore.add({
          type: 'error',
          title: 'Something went wrong, try again.',
        })
      }
    },
  }

  const hubSpotActions: ActionItem[] = [
    ...(canShowHubSpotActions ? [manageTeamsAction] : []),
    ...(canShowRefreshData ? [refreshDataAction] : []),
  ]

  const actionsGroup: ActionItem[] = [
    {
      text: 'Rename',
      icon: 'edit',
      group: 'Actions',
      onAction: () => rename(row),
    },
    {
      text: 'Duplicate',
      icon: 'copy',
      group: 'Actions',
      onAction: () => {
        uiStore.changeRoute({
          path: ClientsPath.create,
          options: {
            state: {
              duplicateId: row.id,
            },
          },
        })
      },
    },
    {
      text: 'Remove',
      icon: 'delete',
      group: 'Actions',
      onAction: () => deleteClient(row, loadData),
    },
  ]

  const actions = !row.accessibleOrganization
    ? [assumeAction, ...hubSpotActions, ...actionsGroup]
    : [assumeAction]

  return actions
}
